import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Pagination,
} from "@mui/material";
import {
  AssignmentTurnedIn,
  BusinessCenter,
  CheckCircle,
  Devices,
  EventBusy,
  ExpandMore,
  FactCheck,
  Person,
  PersonOff,
  Refresh,
  AccountBalance,
  Work,
  AttachMoney,
  Email,
  Phone,
  LocationOn,
  CreditCard,
  School,
  Visibility,
} from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const emptyForm = {
  employee_id: "",
  offboardingType: "resignation",
  resignationDate: "",
  lastWorkingDay: "",
  noticePeriod: "",
  reason: "",
  handoverNotes: "",
  status: "pending",
};

const statusLabels = {
  pending: "Pending",
  in_notice: "In Notice",
  clearance_pending: "Clearance Pending",
  final_settlement_pending: "Final Settlement",
  completed: "Completed",
  cancelled: "Cancelled",
};

const taskStatusLabels = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
  blocked: "Blocked",
  not_applicable: "Not Applicable",
};

const getTokenPayload = () => {
  const token = localStorage.getItem("token");
  if (!token) return { token: null, payload: null };

  try {
    return { token, payload: JSON.parse(atob(token.split(".")[1])) };
  } catch (error) {
    return { token, payload: null };
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "completed":
    case "cleared":
      return "success";
    case "cancelled":
    case "blocked":
      return "error";
    case "in_notice":
    case "clearance_pending":
    case "final_settlement_pending":
    case "partial":
    case "in_progress":
      return "warning";
    default:
      return "default";
  }
};

const formatDate = (dateValue) => {
  if (!dateValue) return "Not set";
  return new Date(dateValue).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (value) => {
  if (!value && value !== 0) return "N/A";
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: '',
    maximumFractionDigits: 0
  }).format(value);
};

const formatStatusText = (value, fallback = "Pending") => {
  if (!value) return fallback;
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const completedTaskStatuses = ["completed", "not_applicable"];

const getDaysUntil = (dateValue) => {
  if (!dateValue) return null;
  const today = new Date();
  const target = new Date(dateValue);
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
};

const getDaysSinceExit = (dateValue) => {
  if (!dateValue) return "N/A";
  const today = new Date();
  const exitDate = new Date(dateValue);
  today.setHours(0, 0, 0, 0);
  exitDate.setHours(0, 0, 0, 0);
  const diffTime = today - exitDate;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays > 0) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  return `In ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? "" : "s"}`;
};

const getEmployeeName = (record) => {
  if (!record) return "Unknown employee";
  const employee = record.employee || record;
  const name = record.employeeName ||
    employee.employeeName ||
    `${employee.firstName || ""} ${employee.lastName || ""}`.trim();
  return name || record.employee_id || employee.employee_id || "Unknown employee";
};

const Offboarding = () => {
  const [{ token, payload }, setAuth] = useState(getTokenPayload);
  const [offboardings, setOffboardings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);
  const [exitedEmployeeDetails, setExitedEmployeeDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tabValue, setTabValue] = useState(0);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const itemsPerPage = 10;

  const role = (payload?.role || localStorage.getItem("role") || "").toLowerCase();
  const companyId = payload?.company_id;
  const isHr = ["hr", "admin"].includes(role);

  const axiosConfig = useMemo(
    () => ({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    }),
    [token]
  );

  const fetchEmployees = useCallback(async () => {
    if (!isHr || !companyId) return;
    const response = await axios.get(
      `${API_URL}/api/viewpersonal?limit=1000`,
      axiosConfig,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    setEmployees(response.data.data || []);
  }, [axiosConfig, companyId, isHr]);

  const fetchSummary = useCallback(async () => {
    if (!isHr || !companyId) return;
    const response = await axios.get(`${API_URL}/api/offboarding/summary`, axiosConfig,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      }
    );
    setSummary(response.data.data || null);
  }, [axiosConfig, companyId, isHr]);

  const fetchOffboardings = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");

    try {
      const queryParams = isHr
        ? `?page=${page}&limit=${itemsPerPage}&search=${encodeURIComponent(searchTerm)}&status=${statusFilter}`
        : ``;

      const endpoint = isHr
        ? `${API_URL}/api/offboarding${queryParams}`
        : `${API_URL}/api/offboarding/my`;

      const response = await axios.get(endpoint, axiosConfig);
      if (isHr && response.data.pages !== undefined) {
        setOffboardings(response.data.data || []);
        setTotalPages(response.data.pages || 1);
      } else {
        setOffboardings(response.data.data || []);
        setTotalPages(1);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load offboarding data");
    } finally {
      setLoading(false);
    }
  }, [axiosConfig, companyId, isHr, page, searchTerm, statusFilter]);

  const fetchExitedEmployeeDetails = useCallback(async (employeeId) => {
    if (!employeeId || !companyId) return;
    setLoadingDetails(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/offboarding/exited-employee/${employeeId}`,
        axiosConfig
      );
      setExitedEmployeeDetails(response.data.data);
    } catch (err) {
      //console.error("Error fetching employee details:", err);
      setError(err.response?.data?.message || "Failed to load employee details");
    } finally {
      setLoadingDetails(false);
    }
  }, [axiosConfig, companyId]);

  const refreshSelectedCase = useCallback(
    async (caseId) => {
      if (!caseId || !companyId) return;
      const response = await axios.get(`${API_URL}/api/offboarding/${caseId}`, axiosConfig);
      const updated = response.data.data;
      setSelectedCase(updated);
      setOffboardings((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    },
    [axiosConfig, companyId]
  );

  useEffect(() => {
    const auth = getTokenPayload();
    setAuth(auth);
    if (auth.payload?.company_id && auth.token) {
      axios.get(`${API_URL}/api/cmp/${auth.payload.company_id}`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      })
        .then(res => {
          if (res.data && res.data.data) {
            setCompanyConfig(res.data.data);
          }
        }).catch(err => 
          {
            // console.error("Error fetching company config:", err)
    });
    }
  }, []);

  useEffect(() => {
    setStatusFilter(tabValue === 0 ? "active" : "completed");
    setPage(1);
  }, [tabValue]);

  useEffect(() => {
    fetchOffboardings();
  }, [fetchOffboardings]);

  useEffect(() => {
    fetchEmployees();
    fetchSummary();
  }, [fetchEmployees, fetchSummary]);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async () => {
    if (!formData.employee_id || !formData.lastWorkingDay) {
      setError("Employee and last working day are required");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await axios.post(`${API_URL}/api/offboarding`, formData, axiosConfig);
      setSuccess("Offboarding case created");
      setFormData(emptyForm);
      setFormOpen(false);
      await fetchOffboardings();
      await fetchSummary();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create offboarding case");
    } finally {
      setSaving(false);
    }
  };

  const handleTaskStatusChange = async (task, status) => {
    if (!selectedCase) return;
    try {
      setSaving(true);
      const response = await axios.put(
        `${API_URL}/api/offboarding/${selectedCase.id}/tasks/${task.id}`,
        { status },
        axiosConfig
      );
      setSelectedCase(response.data.data);
      setOffboardings((prev) => prev.map((item) => (item.id === response.data.data.id ? response.data.data : item)));
      await fetchSummary();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update checklist task");
    } finally {
      setSaving(false);
    }
  };

  const handleCaseStatusChange = async (caseId, status) => {
    if (status === "completed") {
      const confirmed = window.confirm(
        "Complete offboarding now? This will change the employee status to resigned or terminated."
      );
      if (!confirmed) return;
    }

    try {
      setSaving(true);
      setError("");
      const response = await axios.put(
        `${API_URL}/api/offboarding/${caseId}/status`,
        { status },
        axiosConfig
      );
      setSuccess(response.data.message || "Status updated");
      setOffboardings((prev) => prev.map((item) => (item.id === response.data.data.id ? response.data.data : item)));
      setSelectedCase((prev) => (prev?.id === response.data.data.id ? response.data.data : prev));
      await fetchSummary();
    } catch (err) {
      const pendingAssets = err.response?.data?.issuedAssets;
      const baseMessage = err.response?.data?.message || "Failed to update status";
      setError(pendingAssets ? `${baseMessage}. Issued assets pending: ${pendingAssets}` : baseMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleAssetReturned = async (asset) => {
    if (!selectedCase) return;
    try {
      setSaving(true);
      await axios.put(
        `${API_URL}/api/assets/${asset.id}`,
        {
          status: "Returned",
          returnDate: new Date().toISOString().slice(0, 10),
        },
        axiosConfig
      );
      await refreshSelectedCase(selectedCase.id);
      await fetchSummary();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update asset");
    } finally {
      setSaving(false);
    }
  };

  const handleViewExitedEmployee = async (offboardingCase) => {
    setSelectedCase(offboardingCase);
    await fetchExitedEmployeeDetails(offboardingCase.employee_id);
  };

  const selectedEmployee = employees.find((employee) => employee.employee_id === formData.employee_id) || null;

  const summaryCards = [
    { label: "Total Cases", value: summary?.total || offboardings.length, icon: <PersonOff /> },
    { label: "In Notice", value: summary?.in_notice || 0, icon: <EventBusy /> },
    { label: "Pending Tasks", value: summary?.pendingTasks || 0, icon: <FactCheck /> },
    { label: "Completed", value: summary?.completed || 0, icon: <CheckCircle /> },
  ];

  const employeeCase = !isHr ? offboardings[0] || null : null;
  const employeeTasks = employeeCase?.tasks || [];
  const employeeAssets = employeeCase?.assetSummary?.assets || [];
  const completedTasks = employeeTasks.filter((task) => completedTaskStatuses.includes(task.status)).length;
  const checklistProgress = employeeTasks.length ? Math.round((completedTasks / employeeTasks.length) * 100) : 0;
  const daysUntilLastWorkingDay = getDaysUntil(employeeCase?.lastWorkingDay);

  const employeeOverviewCards = employeeCase
    ? [
        {
          label: "Last Working Day",
          value: formatDate(employeeCase.lastWorkingDay),
          helper: daysUntilLastWorkingDay === null
            ? "Date not set"
            : daysUntilLastWorkingDay > 0
              ? `${daysUntilLastWorkingDay} day${daysUntilLastWorkingDay === 1 ? "" : "s"} remaining`
              : daysUntilLastWorkingDay === 0
                ? "Today"
                : "Completed",
          icon: <EventBusy />,
        },
        {
          label: "Checklist",
          value: `${completedTasks}/${employeeTasks.length || 0}`,
          helper: `${checklistProgress}% complete`,
          icon: <AssignmentTurnedIn />,
        },
        {
          label: "Assets",
          value: `${employeeCase.assetSummary?.issued || 0} issued`,
          helper: `${employeeCase.assetSummary?.returned || 0} returned`,
          icon: <Devices />,
        },
        {
          label: "Settlement",
          value: formatStatusText(employeeCase.finalSettlementStatus),
          helper: "Finance clearance",
          icon: <BusinessCenter />,
        },
      ]
    : [];

  if (loading) {
    return (
      <Box sx={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3,  minHeight: "100vh" }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {isHr ? "Offboarding" : "My Offboarding"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isHr
              ? "Track resignation, clearance, assets, documents, and final settlement."
              : "Review your exit timeline, clearance checklist, assets, and settlement progress."}
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Tooltip title="Refresh">
            <Button variant="outlined" startIcon={<Refresh />} onClick={fetchOffboardings}>
              Refresh
            </Button>
          </Tooltip>
          {isHr && (
            <Button variant="contained" startIcon={<PersonOff />} onClick={() => setFormOpen(true)}>
              New Offboarding
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess("")} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {isHr && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {summaryCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.label}>
              <Paper sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb" }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {card.label}
                    </Typography>
                    <Typography variant="h4" fontWeight={700}>
                      {card.value}
                    </Typography>
                  </Box>
                  <Box sx={{ color: "primary.main", display: "flex" }}>{card.icon}</Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {isHr ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                      <Tabs 
              value={tabValue} 
              onChange={(e, newValue) => setTabValue(newValue)} 
              aria-label="offboarding tabs"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label={`Active Cases (${(summary?.total || 0) - (summary?.completed || 0)})`} />
              <Tab label={`Exited Employees (${summary?.completed || 0})`} />
            </Tabs>

          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              label="Search by Employee Name, ID or Email"
              variant="outlined"
              size="small"
              fullWidth
              sx={{ bgcolor: 'white', width: { xs: '100%', sm: '400px' } }}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            />
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2, border: "1px solid #e5e7eb" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Last Working Day</TableCell>
                  {tabValue === 1 && <TableCell>Days Since Exit</TableCell>}
                  <TableCell>Status</TableCell>
                  <TableCell>Assets</TableCell>
                  <TableCell>Final Settlement</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {offboardings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tabValue === 1 ? 8 : 7}>
                      <Box sx={{ py: 5, textAlign: "center" }}>
                        <Typography variant="h6">
                          {tabValue === 0 ? "No active offboarding cases found" : "No exited employees found"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tabValue === 0
                            ? "Create a case when an employee enters exit process."
                            : "Employees will appear here once their offboarding is completed."}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  offboardings.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{getEmployeeName(item)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.employee_id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>{item.offboardingType}</TableCell>
                      <TableCell>{formatDate(item.lastWorkingDay)}</TableCell>
                      {tabValue === 1 && (
                        <TableCell>
                          <Chip
                            label={getDaysSinceExit(item.lastWorkingDay)}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          label={statusLabels[item.status] || item.status}
                          color={getStatusColor(item.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${item.assetSummary?.issued || 0} issued`}
                          color={getStatusColor(item.assetSummary?.clearanceStatus)}
                          size="small"
                          icon={<Devices />}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.finalSettlementStatus?.replace(/_/g, " ") || "pending"}
                          color={getStatusColor(item.finalSettlementStatus)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button 
                          startIcon={<Visibility />} 
                          onClick={() => tabValue === 1 ? handleViewExitedEmployee(item) : setSelectedCase(item)}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages >= 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, v) => setPage(v)}
                color="primary"
              />
            </Box>
          )}
        </>
      ) : employeeCase ? (
        <Stack spacing={3}>
          <Paper
            sx={{
              p: { xs: 2.5, md: 3 },
              borderRadius: 2,
              border: "1px solid #dbe5ef",
              background: "linear-gradient(135deg, #ffffff 0%, #f4fbf7 55%, #eef4ff 100%)",
            }}
          >
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2.5}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Current offboarding case
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {getEmployeeName(employeeCase)}
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  <Chip label={employeeCase.employee_id} size="small" variant="outlined" />
                  <Chip label={formatStatusText(employeeCase.offboardingType)} size="small" variant="outlined" />
                  <Chip
                    label={statusLabels[employeeCase.status] || formatStatusText(employeeCase.status)}
                    color={getStatusColor(employeeCase.status)}
                    size="small"
                  />
                </Stack>
              </Box>
              <Box sx={{ minWidth: { md: 260 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Checklist Progress
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {checklistProgress}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={checklistProgress}
                  sx={{ height: 8, borderRadius: 999, bgcolor: "#dce6ef" }}
                />
              </Box>
            </Stack>
          </Paper>

          <Grid container spacing={2}>
            {employeeOverviewCards.map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.label}>
                <Paper sx={{ p: 2.25, height: "100%", borderRadius: 2, border: "1px solid #e5e7eb" }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {card.label}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ mt: 0.5 }}>
                        {card.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {card.helper}
                      </Typography>
                    </Box>
                    <Box sx={{ color: "primary.main", display: "flex" }}>{card.icon}</Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={7}>
              <Stack spacing={2.5}>
                <Paper sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb" }}>
                  <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
                    <AssignmentTurnedIn color="primary" />
                    <Typography variant="h6">Clearance Checklist</Typography>
                  </Stack>
                  {employeeTasks.length === 0 ? (
                    <Box sx={{ py: 3, textAlign: "center", bgcolor: "#f8fafc", borderRadius: 2 }}>
                      <Typography fontWeight={600}>No checklist tasks assigned yet</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your clearance steps will appear here once HR starts them.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      {employeeTasks.map((task) => {
                        const isDone = completedTaskStatuses.includes(task.status);

                        return (
                          <Box
                            key={task.id}
                            sx={{
                              p: 1.75,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor: isDone ? "#b7e1c1" : "#e5e7eb",
                              bgcolor: isDone ? "#f3fbf5" : "#ffffff",
                            }}
                          >
                            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1.5}>
                              <Stack direction="row" gap={1.25} alignItems="flex-start">
                                <Box sx={{ color: isDone ? "success.main" : "text.secondary", display: "flex", mt: 0.25 }}>
                                  {isDone ? <CheckCircle fontSize="small" /> : <FactCheck fontSize="small" />}
                                </Box>
                                <Box>
                                  <Typography fontWeight={600}>{task.title}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                                    Owner: {task.ownerRole} | Due: {formatDate(task.dueDate)}
                                  </Typography>
                                </Box>
                              </Stack>
                              <Chip
                                label={taskStatusLabels[task.status] || formatStatusText(task.status)}
                                color={getStatusColor(task.status)}
                                size="small"
                              />
                            </Stack>
                            {task.notes && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, pl: { sm: 4 } }}>
                                {task.notes}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Paper>

                <Paper sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb" }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Reason and Handover Notes
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Reason"
                        value={employeeCase.reason || "Not provided"}
                        fullWidth
                        multiline
                        minRows={3}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Handover Notes"
                        value={employeeCase.handoverNotes || "Not provided"}
                        fullWidth
                        multiline
                        minRows={3}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Stack spacing={2.5}>
                <Paper sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb" }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Devices color="primary" />
                      <Typography variant="h6">Asset Clearance</Typography>
                    </Stack>
                    <Chip
                      label={formatStatusText(employeeCase.assetSummary?.clearanceStatus, "Pending")}
                      color={getStatusColor(employeeCase.assetSummary?.clearanceStatus)}
                      size="small"
                    />
                  </Stack>
                  {employeeAssets.length === 0 ? (
                    <Box sx={{ py: 3, textAlign: "center", bgcolor: "#f8fafc", borderRadius: 2 }}>
                      <Typography fontWeight={600}>No assets assigned</Typography>
                      <Typography variant="body2" color="text.secondary">
                        There are no company assets linked to this offboarding case.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1.5}>
                      {employeeAssets.map((asset) => (
                        <Box key={asset.id} sx={{ p: 1.75, borderRadius: 2, border: "1px solid #e5e7eb" }}>
                          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
                            <Box>
                              <Typography fontWeight={700}>{asset.assetName}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {asset.assetType || "Asset"} | Serial: {asset.serialNumber || "N/A"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Issued on {formatDate(asset.issuedDate)}
                              </Typography>
                            </Box>
                            <Chip
                              label={asset.status || "Pending"}
                              color={asset.status === "Returned" ? "success" : "warning"}
                              size="small"
                            />
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Paper>

                <Paper sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb" }}>
                  <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
                    <BusinessCenter color="primary" />
                    <Typography variant="h6">Final Formalities</Typography>
                  </Stack>
                  <Stack spacing={1.5}>
                    <Box sx={{ p: 1.75, borderRadius: 2, bgcolor: "#f8fafc" }}>
                      <Typography variant="body2" color="text.secondary">
                        Final Settlement
                      </Typography>
                      <Chip
                        label={formatStatusText(employeeCase.finalSettlementStatus)}
                        color={getStatusColor(employeeCase.finalSettlementStatus)}
                        size="small"
                        sx={{ mt: 0.75 }}
                      />
                    </Box>
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      ) : (
        <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 2, border: "1px solid #e5e7eb", textAlign: "center" }}>
          <Stack alignItems="center" spacing={1.5}>
            <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: "#eef4ff", display: "grid", placeItems: "center" }}>
              <PersonOff color="primary" />
            </Box>
            <Typography variant="h5" fontWeight={700}>
              No active offboarding case
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
              When HR starts your offboarding, your timeline, checklist, asset clearance, and settlement status will appear here.
            </Typography>
          </Stack>
        </Paper>
      )}

      {/* Create Offboarding Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Offboarding Case</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={employees}
                value={selectedEmployee}
                getOptionLabel={(option) => `${getEmployeeName(option)} (${option.employee_id})`}
                onChange={(_, value) => handleFormChange("employee_id", value?.employee_id || "")}
                renderInput={(params) => <TextField {...params} label="Employee" required />}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Offboarding Type</InputLabel>
                <Select
                  label="Offboarding Type"
                  value={formData.offboardingType}
                  onChange={(event) => handleFormChange("offboardingType", event.target.value)}
                >
                  <MenuItem value="resignation">Resignation</MenuItem>
                  <MenuItem value="termination">Termination</MenuItem>
                  <MenuItem value="retirement">Retirement</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Initial Status</InputLabel>
                <Select
                  label="Initial Status"
                  value={formData.status}
                  onChange={(event) => handleFormChange("status", event.target.value)}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_notice">In Notice</MenuItem>
                  <MenuItem value="clearance_pending">Clearance Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Resignation Date"
                type="date"
                value={formData.resignationDate}
                onChange={(event) => handleFormChange("resignationDate", event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Last Working Day"
                type="date"
                value={formData.lastWorkingDay}
                onChange={(event) => handleFormChange("lastWorkingDay", event.target.value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Notice Period"
                value={formData.noticePeriod}
                onChange={(event) => handleFormChange("noticePeriod", event.target.value)}
                fullWidth
                placeholder="30 days"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reason"
                value={formData.reason}
                onChange={(event) => handleFormChange("reason", event.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Handover Notes"
                value={formData.handoverNotes}
                onChange={(event) => handleFormChange("handoverNotes", event.target.value)}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog with Accordions */}
      <Dialog 
        open={Boolean(selectedCase)} 
        onClose={() => {
          setSelectedCase(null);
          setExitedEmployeeDetails(null);
        }} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="h6">Employee Details</Typography>
              <Typography variant="body2" color="text.secondary">
                {getEmployeeName(selectedCase)} - {selectedCase?.employee_id}
              </Typography>
            </Box>
            {selectedCase && (
              <Chip 
                label={statusLabels[selectedCase.status] || selectedCase.status} 
                color={getStatusColor(selectedCase.status)} 
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedCase && (
            <Stack spacing={3}>
              {/* Offboarding Summary Section */}
              <Paper sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                  Offboarding Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Type</Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ textTransform: "capitalize" }}>
                      {selectedCase.offboardingType}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Resignation Date</Typography>
                    <Typography variant="body2" fontWeight={500}>{formatDate(selectedCase.resignationDate)}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Last Working Day</Typography>
                    <Typography variant="body2" fontWeight={500}>{formatDate(selectedCase.lastWorkingDay)}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Notice Period</Typography>
                    <Typography variant="body2" fontWeight={500}>{selectedCase.noticePeriod || "Not set"}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Accordion Sections - Only for exited employees */}
              {tabValue === 1 && exitedEmployeeDetails && (
                <>
                  {/* Personal Details Accordion */}
             {/* Personal Details Accordion - Updated with Nominee Details */}
<Accordion defaultExpanded>
  <AccordionSummary expandIcon={<ExpandMore />}>
    <Stack direction="row" alignItems="center" gap={1.5}>
      <Avatar sx={{ bgcolor: "primary.main", width: 32, height: 32 }}>
        <Person sx={{ fontSize: 18 }} />
      </Avatar>
      <Typography variant="h6">Personal Details</Typography>
    </Stack>
  </AccordionSummary>
  <AccordionDetails>
    {loadingDetails ? (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    ) : exitedEmployeeDetails.personalDetails ? (
      <Grid container spacing={2}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            <Person fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
            Basic Information
          </Typography>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">Full Name</Typography>
              <Typography variant="body2" fontWeight={500}>
                {exitedEmployeeDetails.personalDetails.fullName || "N/A"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Father's Name</Typography>
              <Typography variant="body2">{exitedEmployeeDetails.personalDetails.fatherName || "N/A"}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Gender</Typography>
              <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                {exitedEmployeeDetails.personalDetails.gender || "N/A"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
              <Typography variant="body2">{formatDate(exitedEmployeeDetails.personalDetails.dateOfBirth)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Anniversary</Typography>
              <Typography variant="body2">{formatDate(exitedEmployeeDetails.personalDetails.anniversary)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Employment Status</Typography>
              <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                {exitedEmployeeDetails.personalDetails.employmentStatus || "N/A"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Tenure</Typography>
              <Typography variant="body2">{exitedEmployeeDetails.personalDetails.tenure || "N/A"}</Typography>
            </Box>
          </Stack>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            <Email fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
            Contact Information
          </Typography>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">Personal Email</Typography>
              <Typography variant="body2">
                <Email fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle", fontSize: 14 }} />
                {exitedEmployeeDetails.personalDetails.personalEmail || "N/A"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Company Email</Typography>
              <Typography variant="body2">
                <Email fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle", fontSize: 14 }} />
                {exitedEmployeeDetails.personalDetails.companyEmail || "N/A"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Phone Number</Typography>
              <Typography variant="body2">
                <Phone fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle", fontSize: 14 }} />
                {exitedEmployeeDetails.personalDetails.phoneNumber || "N/A"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Mobile</Typography>
              <Typography variant="body2">
                <Phone fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle", fontSize: 14 }} />
                {exitedEmployeeDetails.personalDetails.mobile || "N/A"}
              </Typography>
            </Box>
          </Stack>
        </Grid>

        {/* Identification Documents */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            <CreditCard fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
            Identification Documents
          </Typography>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">{companyConfig?.nationalIdLabel || "PAN Number"}</Typography>
              <Typography variant="body2">{exitedEmployeeDetails.personalDetails.panNumber || "N/A"}</Typography>
            </Box>
            {(!companyConfig?.country || companyConfig.country.toLowerCase() === 'india') && (
            <Box>
              <Typography variant="caption" color="text.secondary">Aadhar Number</Typography>
              <Typography variant="body2">{exitedEmployeeDetails.personalDetails.adharCardNumber || "N/A"}</Typography>
            </Box>
            )}
          </Stack>
        </Grid>

        {/* Address */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            <LocationOn fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
            Address
          </Typography>
          {exitedEmployeeDetails.personalDetails.address ? (
            <Typography variant="body2">
              {[
                exitedEmployeeDetails.personalDetails.address.houseNumber,
                exitedEmployeeDetails.personalDetails.address.street,
                exitedEmployeeDetails.personalDetails.address.area,
                exitedEmployeeDetails.personalDetails.address.city,
                exitedEmployeeDetails.personalDetails.address.state,
                exitedEmployeeDetails.personalDetails.address.pinCode
              ].filter(Boolean).join(", ") || "N/A"}
            </Typography>
          ) : (
            <Typography variant="body2">N/A</Typography>
          )}
        </Grid>

        {/* Nominee Details - New Section */}
        {(exitedEmployeeDetails.personalDetails.nominee?.nomineeName ||
          exitedEmployeeDetails.personalDetails.nominee?.relationship ||
          exitedEmployeeDetails.personalDetails.nominee?.nomineeAge ||
          exitedEmployeeDetails.personalDetails.nominee?.individualInsurance ||
          exitedEmployeeDetails.personalDetails.nominee?.groupInsurance) && (
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="primary" gutterBottom>
              <School fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
              Nominee & Insurance Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Nominee Name</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {exitedEmployeeDetails.personalDetails.nominee?.nomineeName || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Relationship</Typography>
                    <Typography variant="body2">
                      {exitedEmployeeDetails.personalDetails.nominee?.relationship || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Nominee Age</Typography>
                    <Typography variant="body2">
                      {exitedEmployeeDetails.personalDetails.nominee?.nomineeAge || "N/A"}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Individual Insurance</Typography>
                    <Typography variant="body2">
                      {exitedEmployeeDetails.personalDetails.nominee?.individualInsurance || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Group Insurance</Typography>
                    <Typography variant="body2">
                      {exitedEmployeeDetails.personalDetails.nominee?.groupInsurance || "N/A"}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    ) : (
      <Typography color="text.secondary">No personal details found</Typography>
    )}
  </AccordionDetails>
</Accordion>

                  {/* Role Details Accordion */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Stack direction="row" alignItems="center" gap={1.5}>
                        <Avatar sx={{ bgcolor: "secondary.main", width: 32, height: 32 }}>
                          <Work sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Typography variant="h6">Role & Responsibilities</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {loadingDetails ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : exitedEmployeeDetails.roleDetails ? (
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="secondary" gutterBottom>Job Information</Typography>
                            <Stack spacing={1}>
                              <Box><Typography variant="caption" color="text.secondary">Designation</Typography><Typography variant="body2" fontWeight={500}>{exitedEmployeeDetails.roleDetails.designation || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Department</Typography><Typography variant="body2">{exitedEmployeeDetails.roleDetails.department || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Role Type</Typography><Typography variant="body2" sx={{ textTransform: "capitalize" }}>{exitedEmployeeDetails.roleDetails.roleType || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Grade</Typography><Typography variant="body2">{exitedEmployeeDetails.roleDetails.grade || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Team Size</Typography><Typography variant="body2">{exitedEmployeeDetails.roleDetails.teamSize || "N/A"}</Typography></Box>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="secondary" gutterBottom>Reporting & Schedule</Typography>
                            <Stack spacing={1}>
                              <Box><Typography variant="caption" color="text.secondary">Reporting Manager</Typography><Typography variant="body2">{exitedEmployeeDetails.roleDetails.reportingManager || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Manager ID</Typography><Typography variant="body2">{exitedEmployeeDetails.roleDetails.manager_id || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Shift Type</Typography><Typography variant="body2" sx={{ textTransform: "capitalize" }}>{exitedEmployeeDetails.roleDetails.shiftType || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Login Time</Typography><Typography variant="body2">{exitedEmployeeDetails.roleDetails.loginTime || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Logout Time</Typography><Typography variant="body2">{exitedEmployeeDetails.roleDetails.logoutTime || "N/A"}</Typography></Box>
                            </Stack>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="secondary" gutterBottom>Responsibilities</Typography>
                            {exitedEmployeeDetails.roleDetails.selectedResponsibilities?.length > 0 ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {exitedEmployeeDetails.roleDetails.selectedResponsibilities.map((resp, idx) => (
                                  <Chip key={idx} label={resp} size="small" variant="outlined" />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2">No responsibilities listed</Typography>
                            )}
                            {exitedEmployeeDetails.roleDetails.additionalResponsibilities && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">Additional Responsibilities</Typography>
                                <Typography variant="body2">{exitedEmployeeDetails.roleDetails.additionalResponsibilities}</Typography>
                              </Box>
                            )}
                          </Grid>
                        </Grid>
                      ) : (
                        <Typography color="text.secondary">No role details found</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>

                  {/* Financial Details Accordion */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Stack direction="row" alignItems="center" gap={1.5}>
                        <Avatar sx={{ bgcolor: "info.main", width: 32, height: 32 }}>
                          <AccountBalance sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Typography variant="h6">Financial Details</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {loadingDetails ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : exitedEmployeeDetails.financialDetails ? (
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="info.main" gutterBottom>Salary & Compensation</Typography>
                            <Stack spacing={1}>
                              <Box><Typography variant="caption" color="text.secondary">Current Salary</Typography><Typography variant="body2" fontWeight={500}>{companyConfig?.currency_symbol || 'â‚¹'}{Number(exitedEmployeeDetails.financialDetails.currentSalary || 0).toLocaleString(undefined)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Previous Salary</Typography><Typography variant="body2">{companyConfig?.currency_symbol || 'â‚¹'}{Number(exitedEmployeeDetails.financialDetails.previousSalary || 0).toLocaleString(undefined)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">CTC</Typography><Typography variant="body2" fontWeight={500}>{companyConfig?.currency_symbol || 'â‚¹'}{Number(exitedEmployeeDetails.financialDetails.ctc || 0).toLocaleString(undefined)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Tax Calculation</Typography><Typography variant="body2">{companyConfig?.currency_symbol || 'â‚¹'}{Number(exitedEmployeeDetails.financialDetails.taxCalculation || 0).toLocaleString(undefined)}</Typography></Box>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="info.main" gutterBottom>Bank Details</Typography>
                            <Stack spacing={1}>
                              <Box><Typography variant="caption" color="text.secondary">Bank Name</Typography><Typography variant="body2">{exitedEmployeeDetails.financialDetails.bankName || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Account Holder Name</Typography><Typography variant="body2">{exitedEmployeeDetails.financialDetails.accountHolderName || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Account Number</Typography><Typography variant="body2">{exitedEmployeeDetails.financialDetails.accountNumber || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Account Type</Typography><Typography variant="body2">{exitedEmployeeDetails.financialDetails.accountType || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Branch</Typography><Typography variant="body2">{exitedEmployeeDetails.financialDetails.branch || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">{companyConfig?.bankCodeLabel || "IFSC Code"}</Typography><Typography variant="body2">{exitedEmployeeDetails.financialDetails.ifscCode || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Credit Card Offered</Typography><Typography variant="body2">{exitedEmployeeDetails.financialDetails.creditCardOffered || "N/A"}</Typography></Box>
                            </Stack>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="info.main" gutterBottom>Offboarding Financials</Typography>
                            <Stack direction="row" spacing={3}>
                              <Box><Typography variant="caption" color="text.secondary">Advance Salary</Typography><Typography variant="body2">{companyConfig?.currency_symbol || 'â‚¹'}{Number(exitedEmployeeDetails.financialDetails.advanceSalary || 0).toLocaleString(undefined)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Resignation Date (Financial)</Typography><Typography variant="body2">{formatDate(exitedEmployeeDetails.financialDetails.resignationDate)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Notice Period</Typography><Typography variant="body2">{exitedEmployeeDetails.financialDetails.noticePeriod || "N/A"}</Typography></Box>
                            </Stack>
                          </Grid>
                        </Grid>
                      ) : (
                        <Typography color="text.secondary">No financial details found</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>

                  {/* Salary Details Accordion */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Stack direction="row" alignItems="center" gap={1.5}>
                        <Avatar sx={{ bgcolor: "success.main", width: 32, height: 32 }}>
                          <AttachMoney sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Typography variant="h6">Salary & Payroll Details</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {loadingDetails ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : exitedEmployeeDetails.payrollDetails ? (
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="success.main" gutterBottom>Earnings</Typography>
                            <Stack spacing={1}>
                              <Box><Typography variant="caption" color="text.secondary">Base Salary</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.base_salary)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">HRA</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.hra)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Special Allowance</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.special_allowance)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Performance Bonus</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.performance_bonus)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Variable Salary</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.variable_salary)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Joining Bonus</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.joining_bonus)}</Typography></Box>
                            </Stack>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="success.main" gutterBottom>Deductions & Benefits</Typography>
                            <Stack spacing={1}>
                              <Box><Typography variant="caption" color="text.secondary">Employee PF</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.employeepf)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Employer PF</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.employerpf)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Professional Tax</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.professional_tax)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Gratuity</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.gratuity)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Medical Insurance</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.medicalInsurance)}</Typography></Box>
                            </Stack>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="success.main" gutterBottom>Summary</Typography>
                            <Stack direction="row" spacing={3} flexWrap="wrap">
                              <Box><Typography variant="caption" color="text.secondary">Gross Salary</Typography><Typography variant="body2" fontWeight={500}>{formatCurrency(exitedEmployeeDetails.payrollDetails.gross_salary)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Taxable Income</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.taxable_income)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Total Tax</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.total_tax)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Monthly Tax</Typography><Typography variant="body2">{formatCurrency(exitedEmployeeDetails.payrollDetails.monthly_tax)}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">Tax Regime</Typography><Typography variant="body2" sx={{ textTransform: "capitalize" }}>{exitedEmployeeDetails.payrollDetails.recommended_tax_regime || "N/A"}</Typography></Box>
                            </Stack>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" color="success.main" gutterBottom>Statutory Information</Typography>
                            <Stack direction="row" spacing={3}>
                              <Box><Typography variant="caption" color="text.secondary">PF Number</Typography><Typography variant="body2">{exitedEmployeeDetails.payrollDetails.pfno || "N/A"}</Typography></Box>
                              <Box><Typography variant="caption" color="text.secondary">UAN</Typography><Typography variant="body2">{exitedEmployeeDetails.payrollDetails.uan || "N/A"}</Typography></Box>
                            </Stack>
                          </Grid>
                        </Grid>
                      ) : (
                        <Typography color="text.secondary">No payroll details found</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </>
              )}

              {/* Show checklist and assets for active cases */}
              {tabValue !== 1 && (
                <>
                  {/* Checklist and Assets sections remain same as before */}
                  <Box>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
                      <AssignmentTurnedIn color="primary" />
                      <Typography variant="h6">Clearance Checklist</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, mt: -0.5 }}>
                      ðŸ’¡ Please update the status of each checklist task below individually. Once all tasks are completed, update the overall Case Status.
                    </Typography>
                    <TableContainer component={Paper} sx={{ border: "1px solid #e5e7eb" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Task</TableCell>
                            <TableCell>Owner</TableCell>
                            <TableCell>Due</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(selectedCase.tasks || []).map((task) => (
                            <TableRow key={task.id}>
                              <TableCell>{task.title}</TableCell>
                              <TableCell sx={{ textTransform: "capitalize" }}>{task.ownerRole}</TableCell>
                              <TableCell>{formatDate(task.dueDate)}</TableCell>
                              <TableCell sx={{ width: 220 }}>
                                {isHr ? (
                                  <FormControl fullWidth size="small">
                                    <Select
                                      value={task.status}
                                      onChange={(event) => handleTaskStatusChange(task, event.target.value)}
                                      disabled={saving || selectedCase.status === "completed"}
                                    >
                                      {Object.entries(taskStatusLabels).map(([value, label]) => (
                                        <MenuItem value={value} key={value}>
                                          {label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                ) : (
                                  <Chip
                                    label={taskStatusLabels[task.status] || task.status}
                                    color={getStatusColor(task.status)}
                                    size="small"
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  <Box>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
                      <Devices color="primary" />
                      <Typography variant="h6">Asset Clearance</Typography>
                      <Chip
                        label={`${selectedCase.assetSummary?.issued || 0} issued`}
                        color={getStatusColor(selectedCase.assetSummary?.clearanceStatus)}
                        size="small"
                      />
                    </Stack>
                    <TableContainer component={Paper} sx={{ border: "1px solid #e5e7eb" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Asset</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Serial</TableCell>
                            <TableCell>Issued</TableCell>
                            <TableCell>Status</TableCell>
                            {isHr && <TableCell align="right">Action</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(selectedCase.assetSummary?.assets || []).length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={isHr ? 6 : 5}>No assets assigned</TableCell>
                            </TableRow>
                          ) : (
                            selectedCase.assetSummary.assets.map((asset) => (
                              <TableRow key={asset.id}>
                                <TableCell>{asset.assetName}</TableCell>
                                <TableCell>{asset.assetType}</TableCell>
                                <TableCell>{asset.serialNumber || "N/A"}</TableCell>
                                <TableCell>{formatDate(asset.issuedDate)}</TableCell>
                                <TableCell>
                                  <Chip label={asset.status} color={asset.status === "Returned" ? "success" : "warning"} size="small" />
                                </TableCell>
                                {isHr && (
                                  <TableCell align="right">
                                    {asset.status !== "Returned" && (
                                      <Button size="small" onClick={() => handleAssetReturned(asset)} disabled={saving}>
                                        Mark Returned
                                      </Button>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {isHr && (
                    <>
                      <Divider />
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Case Status</InputLabel>
                            <Select
                              label="Case Status"
                              value={selectedCase.status}
                              disabled={saving}
                              onChange={(event) => handleCaseStatusChange(selectedCase.id, event.target.value)}
                            >
                              {Object.entries(statusLabels).map(([value, label]) => (
                                <MenuItem key={value} value={value}>
                                  {label}
                                </MenuItem>
                              ))}
                            </Select>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                              ðŸ’¡ Update the overall offboarding status here. Note: You should complete the clearance checklist and return all assets before marking the case as Completed.
                            </Typography>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Stack direction="row" gap={1} flexWrap="wrap">
                            <Chip
                              icon={<BusinessCenter />}
                              label={`Settlement: ${selectedCase.finalSettlementStatus?.replace(/_/g, " ")}`}
                              color={getStatusColor(selectedCase.finalSettlementStatus)}
                            />
                          </Stack>
                        </Grid>
                      </Grid>
                    </>
                  )}
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSelectedCase(null);
            setExitedEmployeeDetails(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Offboarding;